import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import LoginPage from './pages/LoginPage';
import { initSessionManager, clearSessionData } from './utils/sessionManager';
import { isRememberLoginEnabled, getStoredUser } from './utils/googleAuth';
import AlarmPage from './pages/AlarmPage';
import AssignmentsPage from './pages/AssignmentsPage';
import { ASSIGNMENT_EMBEDDED_MODAL_HEIGHT } from './utils/pageZoom';
import AssignmentCardPage from './pages/AssignmentCardPage';
import AssignmentTablePageFinal from './pages/AssignmentTablePageFinal';
import PointsTablePage, { PointsTablePageHandle } from './pages/PointsTablePage';
import ChatPage from './pages/ChatPage';
import BoardPage from './pages/BoardPage';
import HomePage from './pages/HomePage';
import AdminPage from './pages/AdminPage';
import RoutineAssignmentDetailPage from './pages/RoutineAssignmentDetailPage';
import { StudentChatPage } from './pages/StudentChatPage';
import LicensePage from './pages/LicensePage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import Header from './components/Header';
import ProfileEditModal from './components/ProfileEditModal';
import ClassManageModal from './components/ClassManageModal';
import ClassSettingsModal from './components/ClassSettingsModal';
import NewAlarmModal from './components/NewAlarmModal';
import EditSetNameModal from './components/EditSetNameModal';
import AlarmPopup from './components/AlarmPopup';
import PointManagementModal from './components/PointManagementModal';
import ItemCardSettingsModal from './components/ItemCardSettingsModal';
import { SeatAssignmentModal } from './components/SeatAssignmentModal';
import { LevelSettingsModal } from './components/LevelSettingsModal';
import AssignmentSettingsModal from './components/AssignmentSettingsModal';
import MemberProfileSettingsModal from './components/MemberProfileSettingsModal';
import ConfirmModal from './components/ConfirmModal';
import { TimeTag } from './components/TimeTagSettingsModal';
import TimerModal from './components/TimerModal';
import DiceModal from './components/DiceModal';
import AnnouncementModal from './components/AnnouncementModal';
import TermsAgreementModal from './components/TermsAgreementModal';
import { GlobalToast } from './components/GlobalToast';
import { Alarm } from './types/alarm';
import BottomNavigation from './components/BottomNavigation';
import { User, Class, ClassMember } from './types/alarm';
import { saveUserCustomData, loadUserCustomData, clearUserFromStorage } from './utils/userStorage';
import { signOut } from './utils/googleAuth';
import { saveCurrentClassId, loadClasses, loadCurrentClassId } from './utils/storage';
import { userDataService } from './services/userDataService';
import { defaultSetService } from './services/defaultSetService';
import { TimeTagService } from './services/timeTagService';
import { announcementService, Announcement } from './services/announcementService';
import { useGlobalAlarmService } from './hooks/useGlobalAlarmService';
import { sortAlarmsByTime } from './utils/alarmUtils';
import { upgradeService } from './services/upgradeService';
// 마이그레이션 스크립트 로드
import './utils/collectionMigrationRunner';
import './utils/idMigrationRunner';
import './utils/migrationRunner';
import PresentationPage from './pages/PresentationPage';
import './App.css';
import { auth } from './firebase/config';
import { getOrderedMemberGroups } from './utils/memberGroupOrder';
import { applyMinimalConsolePolicy } from './utils/logger';

const USER_STORAGE_KEY = 'alarm-app-user-custom-data';

applyMinimalConsolePolicy();

// CSS 애니메이션 추가
const pulseAnimation = `
  @keyframes pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 4px 12px rgba(var(--color-accent-rgb), 0.3);
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 6px 16px rgba(var(--color-accent-rgb), 0.4);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 4px 12px rgba(var(--color-accent-rgb), 0.3);
    }
  }
`;

// 스타일 태그 추가
const style = document.createElement('style');
style.textContent = pulseAnimation;
document.head.appendChild(style);

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState<'home' | 'alarm' | 'admin' | 'assignments' | 'assignment-table' | 'assignment-card' | 'routine-detail' | 'points-table' | 'chat' | 'board' | 'license' | 'terms-of-service' | 'privacy-policy'>('home');
  /** 클래스챗 재생 중(세트/퀴즈/문제 만들기)이면 다른 페이지 이동 차단 */
  const [chatPlaying, setChatPlaying] = useState(false);
  const [selectedRoutineId, setSelectedRoutineId] = useState<string | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showClassManageModal, setShowClassManageModal] = useState(false);
  /** 메인 페이지에서 "클래스 설정" 바로 열기 시 사용 (클래스 관리 모달 경유) */
  const [initialClassToOpenSettingsForModal, setInitialClassToOpenSettingsForModal] = useState<Class | null>(null);
  /** 클래스 설정 모달만 바로 열기 (클래스 관리 모달 없이) */
  const [showClassSettingsDirect, setShowClassSettingsDirect] = useState(false);
  const [classForSettingsDirect, setClassForSettingsDirect] = useState<Class | null>(null);
  const [themeMode, setThemeMode] = useState<'dark' | 'light'>(() => {
    if (typeof window !== 'undefined') {
      const stored = window.localStorage.getItem('app-theme-mode');
      if (stored === 'dark' || stored === 'light') {
        return stored;
      }
    }
    return 'dark';
  });
  
  // 과제 페이지 변경사항 추적
  const [assignmentCardHasChanges, setAssignmentCardHasChanges] = useState(false);
  const [assignmentTableHasChanges, setAssignmentTableHasChanges] = useState(false);
  const [showAssignmentTableModal, setShowAssignmentTableModal] = useState(false);
  const [showAssignmentCardModal, setShowAssignmentCardModal] = useState(false);
  const [pendingAssignmentModalClose, setPendingAssignmentModalClose] = useState<null | 'table' | 'card'>(null);
  const [pendingNavigation, setPendingNavigation] = useState<{ page: string; saveAndExit: boolean } | null>(null);
  const assignmentCardSaveRef = useRef<(() => Promise<void>) | null>(null);
  const assignmentTableSaveRef = useRef<(() => Promise<void>) | null>(null);
  const isInitializedRef = useRef<boolean>(false);
  
  // 클래스 관련 상태 추가
  const [classes, setClasses] = useState<Class[]>([]);
  const [currentClass, setCurrentClass] = useState<Class | null>(null);
  
  // 알람 페이지 모달 상태들
  const [showNewAlarmModal, setShowNewAlarmModal] = useState(false);
  const [showEditSetNameModal, setShowEditSetNameModal] = useState(false);
  const [showAlarmPopup, setShowAlarmPopup] = useState(false);
  // 사용자가 닫은 알람을 추적 (같은 알람의 다른 타이밍에서도 다시 열리지 않도록)
  const [dismissedAlarms, setDismissedAlarms] = useState<Set<string>>(new Set());
  const [currentAlarmInfo, setCurrentAlarmInfo] = useState<{
    time: string;
    type: '1M' | '3M' | '5M';
    name?: string;
    minutesBefore?: number;
  } | null>(null);
  const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>('12h');
  const [bellIconPosition, setBellIconPosition] = useState<{ bottom: number; right: number } | null>(null);

  // 포인트 페이지 모달 상태들
  const [showSeatAssignmentModal, setShowSeatAssignmentModal] = useState(false);
  const [classMembers, setClassMembers] = useState<ClassMember[]>([]);
  // 멤버 그룹 (기본 A, 로그인 유지/클래스 전환 시 localStorage로 복원)
  const [currentMemberGroup, setCurrentMemberGroup] = useState<string>('A');
  const [showLevelSettingsModal, setShowLevelSettingsModal] = useState(false);
  const [showPointManagementModal, setShowPointManagementModal] = useState(false);
  const [pointManagementModalInitialTab] = useState<'positive' | 'negative'>('positive');
  const [showItemCardSettingsModal, setShowItemCardSettingsModal] = useState(false);
  const [showAssignmentSettingsModal, setShowAssignmentSettingsModal] = useState(false);
  const [showMemberProfileSettingsModal, setShowMemberProfileSettingsModal] = useState(false);
  const [timeTags, setTimeTags] = useState<TimeTag[]>([]);
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [showDiceModal, setShowDiceModal] = useState(false);
  const [timerModalHidden, setTimerModalHidden] = useState(false);
  const [diceModalHidden, setDiceModalHidden] = useState(false);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const isTimerActiveInHover = timerModalHidden && isTimerRunning;
  const [, setAssignmentCategories] = useState<any[]>([]);
  const [assignmentDefaultPoints, setAssignmentDefaultPoints] = useState(10);
  const [assignmentStatusDefaultPoints, setAssignmentStatusDefaultPoints] = useState({
    pending: 0,
    confirmed: 0,
    delayed: 0,
    exempted: 0
  });
  const [authChecked, setAuthChecked] = useState(false);
  
  // 약관 동의 모달 상태
  const [showTermsAgreementModal, setShowTermsAgreementModal] = useState(false);
  const [pendingUserForTermsAgreement, setPendingUserForTermsAgreement] = useState<User | null>(null);
  
  // 공지사항 팝업 상태
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [isAutoPopup, setIsAutoPopup] = useState(true); // 자동 팝업인지 여부

  // 공지사항 목록 상태 (종모양 아이콘 클릭 시 표시)
  const [showAnnouncementList, setShowAnnouncementList] = useState(false);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);

  // 구글/커스텀 양쪽 스토리지에 남은 사용자 캐시 제거
  const clearStoredAuthUser = useCallback(() => {
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('rememberLogin');
      sessionStorage.removeItem('user');
    } catch (e) {
      console.error('로컬 사용자 캐시 정리 실패:', e);
    }
  }, []);

  // 세션이 끊어지거나 권한 오류 시 로컬 상태/스토리지 초기화
  const handleForceLogout = useCallback((reason?: string) => {
    console.log('🔌 세션/권한 오류로 상태 정리', reason);
    clearUserFromStorage();
    clearStoredAuthUser();
    setUser(null);
    setClasses([]);
    setCurrentClass(null);
    setCurrentPage('home');
    setSelectedRoutineId(null);
    setCurrentAlarmInfo(null);
  }, [clearStoredAuthUser]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.theme = themeMode;
    }
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('app-theme-mode', themeMode);
    }
  }, [themeMode]);

  /** 과제 관리 테이블/카드 모달이 열린 동안 뒤 페이지(본문) 스크롤 방지 */
  useEffect(() => {
    const shouldLock =
      !!user &&
      currentPage === 'assignments' &&
      (showAssignmentTableModal || showAssignmentCardModal);

    if (!shouldLock) return;

    const scrollY = window.scrollY;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyPosition = document.body.style.position;
    const prevBodyTop = document.body.style.top;
    const prevBodyWidth = document.body.style.width;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.position = prevBodyPosition;
      document.body.style.top = prevBodyTop;
      document.body.style.width = prevBodyWidth;
      document.documentElement.style.overflow = prevHtmlOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [user, currentPage, showAssignmentTableModal, showAssignmentCardModal]);

  // Google AdSense 스크립트를 프리 플랜 사용자에게만 동적으로 추가
  useEffect(() => {
    const scriptId = 'adsbygoogle-script';
    const isFreeUser = user?.userType === 'free';
    
    // 기존 스크립트 찾기
    const existingAdSense = document.querySelector('script[src*="adsbygoogle.js"]') as HTMLScriptElement | null;
    
    if (isFreeUser) {
      // 프리 플랜 사용자: 스크립트가 없으면 추가
      if (!existingAdSense) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.async = true;
        script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9711372838476542';
        script.crossOrigin = 'anonymous';
        script.onload = () => {
          console.log('✅ Google AdSense 스크립트 로드 완료 (프리 플랜)');
        };
        script.onerror = () => {
          console.error('❌ Google AdSense 스크립트 로드 실패');
        };
        document.head.appendChild(script);
      }
    } else {
      // 유료 플랜 사용자 또는 로그아웃 상태: 스크립트 제거
      if (existingAdSense) {
        existingAdSense.remove();
        console.log('🗑️ Google AdSense 스크립트 제거됨 (유료 플랜 또는 로그아웃)');
      }
    }
    
    // cleanup: 컴포넌트 언마운트 시 스크립트 제거는 하지 않음 (페이지 전체에 적용되어야 하므로)
    return () => {
      // cleanup은 user 상태 변경 시에만 처리
    };
  }, [user?.userType]);

  const handleThemeToggle = useCallback(() => {
    setThemeMode(prev => (prev === 'dark' ? 'light' : 'dark'));
  }, []);
  // 포인트 페이지 ref
  const pointsTablePageRef = useRef<PointsTablePageHandle>(null);

  // 포인트 페이지 메뉴 클릭 핸들러들
  const handleSeatAssignmentClick = useCallback(() => {
    setShowSeatAssignmentModal(true);
  }, []);

  const handleLevelSettingsClick = useCallback(() => {
    setShowLevelSettingsModal(true);
  }, []);


  const handlePointManagementClick = useCallback(() => {
    setShowPointManagementModal(true);
  }, []);

  const handleItemCardSettingsClick = useCallback(() => {
    setShowItemCardSettingsModal(true);
  }, []);

  const handleAssignmentSettingsClick = useCallback(async () => {
    if (user && currentClass) {
      try {
        const loadedTimeTags = await TimeTagService.getTimeTags(user.id, currentClass.id);
        setTimeTags(loadedTimeTags);
      } catch (error) {
        console.error('타임 태그 로드 실패:', error);
        setTimeTags([]);
      }
    }
    setShowAssignmentSettingsModal(true);
  }, [user, currentClass]);

  const handleDiceModalOpen = useCallback(() => {
    setShowDiceModal(true);
    setDiceModalHidden(false);
  }, []);

  const handleMemberProfileSettingsOpen = useCallback(() => {
    setShowMemberProfileSettingsModal(true);
  }, []);

  useEffect(() => {
    const handleOpenByHeaderEvent = () => {
      setShowMemberProfileSettingsModal(true);
    };
    window.addEventListener('openMemberPhotoProfileSettings', handleOpenByHeaderEvent as EventListener);
    return () => {
      window.removeEventListener('openMemberPhotoProfileSettings', handleOpenByHeaderEvent as EventListener);
    };
  }, []);

  // 알람 팝업 열기 핸들러 (useGlobalAlarmService보다 먼저 정의)
  const handleAlarmPopupOpen = useCallback((alarmInfo?: {
    time: string;
    type: '1M' | '3M' | '5M';
    name?: string;
    minutesBefore?: number;
  }) => {
    if (alarmInfo) {
      // 사용자가 이미 이 알람을 닫았는지 확인
      const alarmKey = `${alarmInfo.time}-${alarmInfo.type}`;
      if (dismissedAlarms.has(alarmKey)) {
        console.log(`🔕 [${alarmInfo.type}] 알람 팝업 닫힘 상태 유지:`, alarmKey);
        return false;
      }
      setCurrentAlarmInfo({
        time: alarmInfo.time,
        type: alarmInfo.type,
        name: alarmInfo.name,
        minutesBefore: alarmInfo.minutesBefore
      });
      setShowAlarmPopup(true);
      return true;
    }
    console.log('🔕 알람 정보가 없어 팝업을 열지 않음');
    return false;
  }, [dismissedAlarms]);

  // 전역 알람 서비스 사용
  const globalAlarmService = useGlobalAlarmService({
    user,
    onAlarmPopupOpen: handleAlarmPopupOpen
  });

  const {
    alarmSets,
    setAlarmSets,
    loadUserAlarmData,
    stopAlarm
  } = globalAlarmService;

  // 타임 태그로부터 알람 생성 핸들러
  const handleCreateAlarmsFromTimeTags = useCallback(async (alarmsData: { alarm: Alarm; setIndex: number; dayIndex: number }[]) => {
    if (!user || !currentClass || !globalAlarmService) return;

    const { alarmSets, setAlarmSets } = globalAlarmService;
    
    // 알람 세트 업데이트
    const updatedAlarmSets = alarmSets.map((set, setIdx) => {
      // 해당 세트의 알람 목록 가져오기
      const alarmsForThisSet = alarmsData.filter(a => a.setIndex === setIdx);
      
      if (alarmsForThisSet.length === 0) {
        return set;
      }

      // 각 요일에 대해 기존 알람 삭제 후 새 알람 추가
      const dayIndices = alarmsForThisSet.map(a => a.dayIndex);
      let updatedAlarms = set.alarms.filter((alarm: Alarm) => {
        // 해당 요일에 속하는 알람만 제외
        return !dayIndices.some(dayIdx => alarm.days.includes(dayIdx));
      });

      // 새 알람 추가
      alarmsForThisSet.forEach(({ alarm: newAlarm }) => {
        updatedAlarms.push(newAlarm);
      });

      // 시간순으로 정렬
      updatedAlarms = sortAlarmsByTime(updatedAlarms);

      return {
        ...set,
        alarms: updatedAlarms
      };
    });

    setAlarmSets(updatedAlarmSets);

    // Firebase에 저장
    try {
      await userDataService.saveClassAlarmSets(user.id, currentClass.id, updatedAlarmSets);
      console.log('타임 태그로부터 생성된 알람 저장 완료');
    } catch (error) {
      console.error('타임 태그로부터 생성된 알람 저장 실패:', error);
      // 실패 시 로컬 스토리지에 저장
      const { saveClassAlarmSets } = await import('./utils/storage');
      saveClassAlarmSets(updatedAlarmSets, user.id, currentClass.id);
    }
  }, [user, currentClass, globalAlarmService]);

  // 멤버 정보 변경 시 호출될 콜백
  const handleMembersChange = useCallback(() => {
    console.log('🔄 멤버 정보 변경 감지 - 과제 페이지 데이터 새로고침 필요');
    // 과제 페이지가 현재 페이지인 경우에만 새로고침
    if (currentPage === 'assignments') {
      // 강제로 과제 페이지 데이터 새로고침
      // 이는 AssignmentsPage의 onMembersChange prop을 통해 처리됨
      console.log('✅ 과제 페이지 데이터 새로고침 실행');
    }
  }, [currentPage]);

  // 클래스 데이터 로드 함수
  const loadClassData = useCallback(async (userId: string, userCurrentClassId?: string) => {
    try {
      console.log('📚 App 클래스 데이터 로드 시작:', { 
        userId, 
        userCurrentClassId 
      });
      
      // 컬렉션에서 클래스 목록 로드
      const userClasses = await userDataService.loadUserClasses(userId);
      
      console.log('✅ App 클래스 로드 완료:', {
        classesCount: userClasses.length
      });
      setClasses(userClasses);
      
      // 현재 클래스 ID 확인 (userCurrentClassId 우선 사용)
      let currentClassId: string | undefined = userCurrentClassId;
      if (!currentClassId) {
        currentClassId = await userDataService.loadCurrentClassId(userId) || undefined;
      }
      
      console.log('✅ App 현재 클래스 ID 확인:', { currentClassId });
      
      if (currentClassId && userClasses.length > 0) {
        const foundClass = userClasses.find(cls => cls.id === currentClassId);
        console.log('🔍 App 현재 클래스 설정:', { 
          currentClassId, 
          foundClass: foundClass ? {
            id: foundClass.id,
            name: foundClass.name,
            coinSettings: foundClass.coinSettings
          } : null
        });
        setCurrentClass(foundClass || userClasses[0]);
      } else if (userClasses.length > 0) {
        console.log('🔍 App 첫 번째 클래스를 현재 클래스로 설정:', {
          firstClass: {
            id: userClasses[0].id,
            name: userClasses[0].name,
            coinSettings: userClasses[0].coinSettings
          }
        });
        setCurrentClass(userClasses[0]);
        // 첫 번째 클래스를 현재 클래스로 설정
        await userDataService.saveCurrentClassId(userId, userClasses[0].id);
      }
    } catch (error) {
      console.error('❌ App 클래스 데이터 로드 실패:', error);
      // Firestore 실패 시 로컬 스토리지에서 로드
      const userClasses = loadClasses(userId);
      setClasses(userClasses);
      
      const currentClassId = loadCurrentClassId(userId);
      if (currentClassId && userClasses.length > 0) {
        const foundClass = userClasses.find(cls => cls.id === currentClassId);
        setCurrentClass(foundClass || userClasses[0]);
      } else if (userClasses.length > 0) {
        setCurrentClass(userClasses[0]);
        saveCurrentClassId(userId, userClasses[0].id);
      }
    }
  }, []); // 빈 dependency array로 안정성 확보

  // 클래스 멤버 로드 함수
  const loadClassMembers = useCallback(async () => {
    if (!user || !currentClass) return;

    try {
      console.log('🔍 App 클래스 멤버 로드 시작:', { userId: user.id, classId: currentClass.id, memberGroup: currentMemberGroup });
      const members = await userDataService.loadClassMembers(user.id, currentClass.id, currentMemberGroup);
      console.log('✅ App 클래스 멤버 로드 완료:', { membersCount: members.length });
      setClassMembers(members);
    } catch (error) {
      console.error('❌ App 클래스 멤버 로드 실패:', error);
    }
  }, [user, currentClass, currentMemberGroup]);

  // 현재 클래스의 멤버 그룹 목록 (없으면 A만)
  const availableMemberGroups = useMemo(() => {
    return getOrderedMemberGroups(currentClass?.memberGroups, currentClass?.memberGroupOrder);
  }, [currentClass]);

  // 클래스/유저가 바뀔 때 선택 그룹 복원
  useEffect(() => {
    if (!user || !currentClass) return;
    const key = `selected_member_group_${user.id}_${currentClass.id}`;
    const saved = localStorage.getItem(key);
    const normalized = (saved || 'A').toString().trim().toUpperCase();
    const nextGroup = availableMemberGroups.includes(normalized) ? normalized : (availableMemberGroups[0] || 'A');
    setCurrentMemberGroup(nextGroup);
  }, [user, currentClass, availableMemberGroups]);

  const handleMemberGroupChange = useCallback((group: string) => {
    if (!user || !currentClass) return;
    const next = (group || 'A').toString().trim().toUpperCase();
    const safeNext = availableMemberGroups.includes(next) ? next : (availableMemberGroups[0] || 'A');
    setCurrentMemberGroup(safeNext);
    const key = `selected_member_group_${user.id}_${currentClass.id}`;
    localStorage.setItem(key, safeNext);
  }, [user, currentClass, availableMemberGroups]);

  // currentClass / currentMemberGroup가 변경될 때 클래스 멤버 로드
  useEffect(() => {
    if (currentClass) {
      loadClassMembers();
    }
  }, [currentClass, currentMemberGroup, loadClassMembers]);

  // 앱 시작 시 저장된 사용자 정보 불러오기 (한 번만 실행)
  useEffect(() => {
    // 이미 초기화되었거나 auth 체크 전이면 실행하지 않음
    if (isInitializedRef.current || !authChecked) {
      return;
    }
    // Firebase 세션이 없으면 저장된 사용자로 자동 복원하지 않음
    if (!auth.currentUser) {
      isInitializedRef.current = true;
      return;
    }
    
    const initializeApp = async () => {
      // user가 이미 설정되어 있으면 초기화만 표시하고 종료
      if (user !== null) {
        isInitializedRef.current = true;
        return;
      }
      
      const savedUser = loadUserCustomData();
      // 다른 탭에서 로그아웃되어 Firebase 세션이 없으면 저장된 사용자도 무시/정리
      if (!auth.currentUser) {
        if (savedUser) {
          clearUserFromStorage();
        }
        isInitializedRef.current = true;
        return;
      }
      if (savedUser) {
        console.log('💾 앱 시작 시 사용자 정보 로드:', savedUser);
        setUser(savedUser);
        // 사용자 정보가 로드되면 클래스 데이터도 로드
        await loadClassData(savedUser.id, savedUser.currentClassId);
      }
      isInitializedRef.current = true;
    };
    
    initializeApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked]); // auth 체크 이후 한 번만 실행

  const handleLogin = async (userData: User) => {
    try {
      console.log('🔐 로그인 시작:', { userId: userData.id });
      // Firestore에서 사용자 프로필 로드
      const userProfileFromFirestore = await userDataService.loadUserProfile(userData.id);
      console.log('🔍 Firestore에서 로드된 사용자 프로필:', userProfileFromFirestore);
      
      // 만료된 이용권 체크 및 자동 제거
      const { licenseService } = await import('./services/licenseService');
      const expiredCheck = await licenseService.checkAndRemoveExpiredLicense(userData.id);
      if (expiredCheck.removed) {
        console.log('⚠️ 만료된 이용권이 자동으로 제거되었습니다.');
        // Firestore에서 다시 로드 (이용권이 제거된 상태)
        const updatedProfile = await userDataService.loadUserProfile(userData.id);
        if (updatedProfile) {
          Object.assign(userProfileFromFirestore || {}, updatedProfile);
        }
      }
      
      // 클래스 목록 로드
      const userClasses = await userDataService.loadUserClasses(userData.id);
      console.log('🔍 컬렉션에서 로드된 클래스:', userClasses.length, '개');
      
      // 저장된 커스텀 정보 불러오기 (로컬 백업용)
      const savedCustomData = loadUserCustomData();
      
      // 사용자 프로필 병합
      let mergedUser = {
        ...userData,
        // Google 계정의 실제 이름을 우선적으로 사용
        name: userData.name || userProfileFromFirestore?.name || savedCustomData?.name || userData.email?.split('@')[0] || '사용자',
        picture: userProfileFromFirestore?.picture || savedCustomData?.picture || userData.picture,

        userType: userProfileFromFirestore?.userType || savedCustomData?.userType || userData.userType || 'free',
        currentClassId: userProfileFromFirestore?.currentClassId || savedCustomData?.currentClassId,
        license: userProfileFromFirestore?.license || savedCustomData?.license,
        termsAgreed: userProfileFromFirestore?.termsAgreed,
        termsAgreedAt: userProfileFromFirestore?.termsAgreedAt
      };
      
      // 기본 클래스 생성 (컬렉션에 클래스가 없는 경우)
      if (userClasses.length === 0) {
        const defaultClassName = (mergedUser.name ? `${mergedUser.name}의 클래스` : '기본 클래스').slice(0, 15);
        const defaultClass: Class = {
          id: `class_${Date.now()}`,
          name: defaultClassName,
          memberCount: 0,
          memberGroups: ['A'],
          classEntryCode: '0000',
          createdAt: new Date(),
          isActive: true
        };
        
        console.log('📝 기본 클래스 생성 중:', defaultClass.name);
        
        // 기본 클래스를 컬렉션에 저장
        await userDataService.saveClass(mergedUser.id, defaultClass);
        
        // 기본 알람셋 생성
        const defaultAlarmSet = {
          id: 'default-alarm-set',
          name: '기본 알람',
          alarms: [],
          isActive: true,
          createdAt: Date.now()
        };
        await userDataService.saveClassAlarmSets(mergedUser.id, defaultClass.id, [defaultAlarmSet]);
        
        // currentClassId 설정
        mergedUser.currentClassId = defaultClass.id;
        await userDataService.saveCurrentClassId(mergedUser.id, defaultClass.id);
        
        console.log('✅ 기본 클래스와 알람셋 생성 완료');
      } else {
        // 기존 클래스가 있으면 currentClassId 확인
        if (!mergedUser.currentClassId) {
          mergedUser.currentClassId = userClasses[0].id;
          await userDataService.saveCurrentClassId(mergedUser.id, userClasses[0].id);
        }
      }
      
      // 사용자 프로필 저장
      await userDataService.saveUserProfile(mergedUser);
      
      // 사용자 권한 검증 (업그레이드 코드 유효성 확인)
      const validatedUserType = await upgradeService.validateUserPermissions(mergedUser);
      if (validatedUserType !== mergedUser.userType) {
        // 권한이 변경된 경우 사용자 정보 업데이트
        mergedUser.userType = validatedUserType;
        await userDataService.saveUserProfile(mergedUser);
        console.log('🔄 권한 검증 후 업데이트:', { 
          userId: mergedUser.id, 
          newUserType: validatedUserType 
        });
      }
      
      // 약관 동의 확인 (모든 문서 생성 후 마지막에 확인)
      // termsAgreed가 명시적으로 true가 아닌 경우 모달 표시
      if (mergedUser.termsAgreed !== true) {
        console.log('📋 약관 동의 필요, 모달 표시 (프로필/클래스는 이미 생성됨):', { 
          termsAgreed: mergedUser.termsAgreed,
          userProfileFromFirestore: userProfileFromFirestore?.termsAgreed 
        });
        // 프로필과 클래스는 이미 생성된 상태
        setPendingUserForTermsAgreement(mergedUser);
        setShowTermsAgreementModal(true);
        // 약관 동의 완료 전까지는 로그인 완료 처리하지 않음
        return;
      }
      
      console.log('✅ 약관 동의 완료됨, 모달 표시 안 함:', { termsAgreed: mergedUser.termsAgreed });
      
      console.log('✅ 로그인 완료:', { 
        userId: mergedUser.id, 
        userType: mergedUser.userType,
        currentClassId: mergedUser.currentClassId
      });
      
      // 사용자 상태 설정
      setUser(mergedUser);
      
      // 로컬 스토리지에도 백업 저장 (userType 포함)
      saveUserCustomData(mergedUser);
      
      // 클래스 데이터 로드
      await loadClassData(mergedUser.id, mergedUser.currentClassId);
      
      // authChecked를 true로 설정하여 로그인 페이지가 사라지도록 함
      setAuthChecked(true);
      
      // 로그인 후 항상 홈페이지로 이동
      setCurrentPage('home');
    } catch (error) {
      console.error('❌ 로그인 중 오류:', error);
      // 실패 시 세션/상태를 정리하고 로그인 페이지 유지
      clearUserFromStorage();
      clearStoredAuthUser();
      setUser(null);
      setClasses([]);
      setCurrentClass(null);
      setCurrentPage('home');
      try {
        await firebaseSignOut(auth);
      } catch {
        /* ignore */
      }
    }
  };

  const handleTermsAgreementConfirm = async (agreedTerms: boolean, agreedPrivacy: boolean) => {
    if (!pendingUserForTermsAgreement) return;
    
    try {
      // 약관 동의 정보를 사용자 프로필에 저장
      // 프로필과 클래스는 이미 handleLogin에서 생성되었으므로 약관 동의 정보만 업데이트
      const updatedUser = {
        ...pendingUserForTermsAgreement,
        termsAgreed: true,
        termsAgreedAt: new Date().toISOString()
      };
      
      // Firestore에 약관 동의 정보만 저장 (프로필은 이미 생성됨)
      await userDataService.saveUserProfile(updatedUser);
      
      console.log('✅ 약관 동의 정보 저장 완료');
      
      // 약관 동의 완료 후 로그인 처리 계속 진행
      setUser(updatedUser);
      saveUserCustomData(updatedUser);
      
      // 클래스 데이터 로드 (이미 생성되어 있음)
      await loadClassData(updatedUser.id, updatedUser.currentClassId);
      
      // authChecked를 true로 설정하여 로그인 페이지가 사라지도록 함
      setAuthChecked(true);
      
      // 로그인 후 항상 홈페이지로 이동
      setCurrentPage('home');
      
      // 모달 닫기
      setShowTermsAgreementModal(false);
      setPendingUserForTermsAgreement(null);
    } catch (error) {
      console.error('약관 동의 저장 실패:', error);
      // 에러 발생 시에도 모달은 닫고 다시 시도할 수 있도록 함
      setShowTermsAgreementModal(false);
      setPendingUserForTermsAgreement(null);
    }
  };

  const handleTermsAgreementClose = () => {
    // 약관 동의 모달을 닫으면 로그아웃 처리
    console.log('⚠️ 약관 동의 취소, 로그아웃 처리');
    setShowTermsAgreementModal(false);
    setPendingUserForTermsAgreement(null);
    handleLogout();
  };

  const handleTermsAgreementWithdraw = async (userEmail: string) => {
    if (!pendingUserForTermsAgreement) return;

    const userId = pendingUserForTermsAgreement.id;
    try {
      console.log('🗑️ 약관 동의 모달에서 회원탈퇴 시작');
      // 1. 반드시 Auth 삭제만 먼저 시도. 실패 시(예: 최근 로그인 필요) Firestore/DB는 절대 삭제하지 않음.
      const { deleteFirebaseUser } = await import('./utils/userStorage');
      await deleteFirebaseUser();

      // 2. Auth 삭제 성공 후에만 Firestore 사용자 데이터 삭제
      await userDataService.deleteUserAccount(userId);

      // 로컬 스토리지 정리
      clearUserFromStorage();
      clearStoredAuthUser();

      // 모달 닫기
      setShowTermsAgreementModal(false);
      setPendingUserForTermsAgreement(null);

      // 로그아웃 처리
      setUser(null);
      setClasses([]);
      setCurrentClass(null);
      setAuthChecked(true);
      setCurrentPage('home');
      
      console.log('✅ 회원탈퇴 완료');
    } catch (error: any) {
      console.error('회원탈퇴 실패:', error);
      throw error; // TermsAgreementModal에서 에러 처리
    }
  };

  const handleLogout = async () => {
    console.log('🔐 로그아웃 시작');
    
    // 세션 데이터 정리
    clearSessionData();
    
    // 먼저 로컬 스토리지에서 사용자 정보 정리 (Firestore 데이터는 유지)
    clearUserFromStorage();
    
    // 클래스 연동 설정 초기화 (다른 클래스 데이터 가져오기 버튼 활성화 상태 제거)
    localStorage.removeItem('isImportButtonEnabled');
    
    // Firebase 로그아웃 처리
    try {
      await signOut();
    } catch (error) {
      console.error('Firebase 로그아웃 실패:', error);
    }
    
    // 사용자 상태를 null로 설정 (자동 로그인 방지)
    setUser(null);
    
    // 초기화 플래그는 유지하여 useEffect가 다시 실행되지 않도록 함
    // (다음 앱 시작 시에만 자동 로그인 시도)
    
    setCurrentPage('home');
  };

  // Firebase Auth 상태가 변경되면(로그인/로그아웃) 세션 상태만 신뢰
  useEffect(() => {
    // 타임아웃 설정: 10초 후에도 인증 확인이 안 되면 로딩 화면을 표시
    const timeoutId = setTimeout(() => {
      if (!authChecked) {
        console.warn('⚠️ 인증 확인 타임아웃, 기본 상태로 진행');
        setAuthChecked(true);
      }
    }, 10000);

    const unsubscribe = onAuthStateChanged(
      auth,
      (fbUser) => {
        clearTimeout(timeoutId);
        if (!fbUser) {
          // 세션 없음 → 상태/스토리지 정리 후 홈
          handleForceLogout('auth-state-null');
          setAuthChecked(true);
          return;
        }

        // 이미 로그인 처리 중이거나 사용자 상태가 설정된 경우 무시 (handleLogin이 처리 중)
        // handleLogin이 먼저 실행되어 사용자 상태를 설정한 경우 onAuthStateChanged는 건너뜀
        // 또는 로컬 스토리지에 사용자 정보가 있는 경우도 건너뜀 (handleLogin이 곧 실행될 것)
        const storedUser = getStoredUser();
        if ((user && user.id === fbUser.uid) || (storedUser && storedUser.id === fbUser.uid)) {
          console.log('✅ 이미 로그인 처리됨 또는 처리 중, onAuthStateChanged 건너뜀');
          setAuthChecked(true);
          return;
        }

        // 세션 있음 → 서버에서 프로필/클래스 로드
        const loadSessionData = async () => {
          try {
            console.log('🔐 세션 발견, 프로필/클래스 로드 시작:', fbUser.uid);
            const userProfileFromFirestore = await userDataService.loadUserProfile(fbUser.uid);
            console.log('🔍 Firestore에서 로드된 사용자 프로필:', userProfileFromFirestore);
            
            const userClasses = await userDataService.loadUserClasses(fbUser.uid);
            console.log('🔍 컬렉션에서 로드된 클래스:', userClasses.length, '개');

            // 프로필이 없고 클래스도 없는 경우 (신규 사용자) → handleLogin이 처리하도록 대기
            // 이 경우 로그아웃하지 않고 authChecked만 true로 설정
            if (!userProfileFromFirestore && userClasses.length === 0) {
              console.log('⚠️ 신규 사용자로 보임, handleLogin이 처리할 때까지 대기');
              setAuthChecked(true);
              return;
            }

          const mergedUser: User = {
            id: fbUser.uid,
            email: fbUser.email || userProfileFromFirestore?.email || '',
            name: userProfileFromFirestore?.name || fbUser.displayName || '사용자',
            picture: userProfileFromFirestore?.picture || fbUser.photoURL || '',
            userType: userProfileFromFirestore?.userType || 'free',
            currentClassId: userProfileFromFirestore?.currentClassId || userClasses[0]?.id,
          };

          // 상태 세팅
          setUser(mergedUser);
          setClasses(userClasses);
          const targetClass = userClasses.find(c => c.id === mergedUser.currentClassId) || userClasses[0] || null;
          setCurrentClass(targetClass || null);
          saveUserCustomData(mergedUser);
        } catch (error) {
          console.error('❌ 세션 데이터 로드 실패:', error);
          // 에러 발생 시 로그아웃하지 않고 authChecked만 true로 설정
          // handleLogin이 처리할 수 있도록 함
          setAuthChecked(true);
        } finally {
          setAuthChecked(true);
        }
      };

      void loadSessionData();
    }, (error) => {
      // 인증 상태 변경 중 에러 발생 시
      console.error('❌ Firebase Auth 상태 확인 중 에러:', error);
      clearTimeout(timeoutId);
      setAuthChecked(true);
      // 에러 발생 시에도 강제 로그아웃하지 않음 (로그인 처리 중일 수 있음)
    });
    
    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [authChecked, handleForceLogout, user]);

  // 로컬 스토리지에서 사용자 데이터가 제거되는 경우(다른 탭 로그아웃) 감지
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      const isUserKeyCleared =
        (event.key === USER_STORAGE_KEY && event.newValue === null) ||
        (event.key === 'user' && event.newValue === null) ||
        (event.key === 'rememberLogin' && event.newValue === null);
      if (isUserKeyCleared && user) {
        handleForceLogout('storage-cleared');
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [user, handleForceLogout]);

  // 세션 만료 관리: 사용자가 로그인한 경우에만 세션 추적 시작
  useEffect(() => {
    if (!user) {
      return; // 사용자가 로그인하지 않은 경우 세션 관리 불필요
    }

    console.log('⏰ 세션 만료 관리 시작');
    const isRememberMe = isRememberLoginEnabled();
    console.log(`⏰ 로그인 유지 옵션: ${isRememberMe ? '예 (12시간)' : '아니오 (4시간)'}`);

    // 세션 관리자 초기화
    const cleanup = initSessionManager({
      onSessionExpired: () => {
        console.log('⏰ 세션 만료로 인한 자동 로그아웃');
        alert('세션이 만료되었습니다. 계속하려면 다시 로그인 하세요.');
        handleLogout();
      },
      isRememberMe
    });

    // 컴포넌트 언마운트 또는 사용자 로그아웃 시 정리
    return () => {
      console.log('⏰ 세션 만료 관리 종료');
      cleanup();
    };
  }, [user]); // user 상태가 변경될 때마다 재설정

  // 사용자가 사라지면 즉시 홈으로 페이지 전환하여 잔여 페이지 깜빡임 방지
  useEffect(() => {
    if (!user && currentPage !== 'home') {
      setCurrentPage('home');
    }
  }, [user, currentPage]);

  // 사용자만 있고 클래스가 없는 비정상 상태도 홈으로 복귀
  useEffect(() => {
    if (authChecked && user && !currentClass && currentPage !== 'home') {
      setCurrentPage('home');
    }
  }, [authChecked, user, currentClass, currentPage]);

  // 공지사항 팝업 표시 로직
  useEffect(() => {
    const checkAndShowAnnouncement = async () => {
      console.log('🔍 공지사항 자동 팝업 체크 시작');
      console.log('authChecked:', authChecked, 'user:', user ? user.email : null);

      // 로그인 상태가 아니면 표시하지 않음
      if (!authChecked || !user) {
        console.log('❌ 로그인 상태가 아니므로 공지사항 팝업 생략');
        return;
      }

      // 오늘 날짜 확인
      const today = new Date().toDateString();
      const lastShownDate = localStorage.getItem('announcement-last-shown-date');
      console.log('오늘 날짜:', today, '마지막 표시 날짜:', lastShownDate);

      // 개발/테스트용: 공지사항 팝업을 강제로 표시하려면 아래 줄을 주석 해제
      // localStorage.removeItem('announcement-last-shown-date');
      // console.log('🔄 공지사항 팝업 강제 표시를 위해 localStorage 초기화');

      // 오늘 이미 표시했거나 "오늘 하루 보지 않기"를 선택했으면 표시하지 않음
      if (lastShownDate === today) {
        console.log('ℹ️ 오늘 이미 공지사항 표시됨, 팝업 생략');
        console.log('💡 팝업을 강제로 테스트하려면 브라우저 콘솔에서 다음 명령어 실행:');
        console.log('   localStorage.removeItem(\'announcement-last-shown-date\'); location.reload();');
        console.log('💡 또는 AdminPage에서 새로운 공지사항을 생성/수정해보세요');
        return;
      }

      try {
        console.log('📡 공지사항 목록 가져오는 중...');
        // 공지사항 목록 가져오기 (최신 공지사항만 팝업 표시)
        const announcementsData = await announcementService.getAnnouncements();
        console.log('📋 가져온 공지사항 수:', announcementsData.length);

        if (announcementsData.length === 0) {
          console.log('⚠️ Firestore에서 공지사항 데이터를 찾을 수 없음');
          console.log('💡 AdminPage에서 공지사항을 생성하고 활성화했는지 확인하세요');
        }

        if (announcementsData.length > 0) {
          console.log('📋 공지사항 목록 상세:', announcementsData.map(a => ({
            id: a.id,
            title: a.title,
            isActive: a.isActive,
            contentLength: a.content.length,
            contentPreview: a.content.substring(0, 50) + (a.content.length > 50 ? '...' : ''),
            createdAt: a.createdAt.toISOString()
          })));
        }

        const activeAnnouncements = announcementsData.filter(a => a.isActive && a.content.trim());
        console.log('✅ 활성화된 공지사항 수:', activeAnnouncements.length);

        if (activeAnnouncements.length === 0) {
          console.log('⚠️ 활성화된 공지사항이 없음 - 모든 공지사항이 비활성화되었거나 내용이 비어있음');
          console.log('💡 AdminPage에서 공지사항을 생성하고 "활성화" 상태로 설정했는지 확인하세요');
        }

        if (activeAnnouncements.length > 0) {
          console.log('✅ 활성화된 공지사항:', activeAnnouncements.map(a => ({
            id: a.id,
            title: a.title,
            contentPreview: a.content.substring(0, 100) + (a.content.length > 100 ? '...' : ''),
            createdAt: a.createdAt.toISOString()
          })));
        }

        if (activeAnnouncements.length > 0) {
          // 가장 최신 공지사항만 표시
          const latestAnnouncement = activeAnnouncements[0];
          console.log('🎯 표시할 공지사항:', latestAnnouncement.title || '제목 없음');

          setAnnouncement(latestAnnouncement);
          setIsAutoPopup(true); // 자동 팝업 표시
          setShowAnnouncementModal(true);
          // 오늘 날짜 저장
          localStorage.setItem('announcement-last-shown-date', today);
          console.log('✅ 공지사항 팝업 표시 완료');
        } else {
          console.log('ℹ️ 표시할 활성화된 공지사항이 없음');
        }
      } catch (error) {
        console.error('❌ 공지사항 가져오기 실패:', error);
      }
    };

    checkAndShowAnnouncement();
  }, [authChecked, user]);

  // 공지사항 팝업 닫기 핸들러
  const handleAnnouncementClose = () => {
    setShowAnnouncementModal(false);
  };

  // 오늘 하루 보지 않기 핸들러
  const handleDontShowToday = () => {
    const today = new Date().toDateString();
    localStorage.setItem('announcement-last-shown-date', today);
    setShowAnnouncementModal(false);
  };

  // 공지사항 목록 열기 핸들러 (종모양 아이콘 클릭 시)
  const handleAnnouncementListOpen = async () => {
    try {
      const announcementsData = await announcementService.getAnnouncements();
      const activeAnnouncements = announcementsData.filter(a => a.isActive && a.content.trim());
      setAnnouncements(activeAnnouncements);
      setShowAnnouncementList(true);
    } catch (error) {
      console.error('공지사항 목록 가져오기 실패:', error);
      alert('공지사항 목록을 불러오는데 실패했습니다.');
    }
  };

  // 공지사항 목록 닫기 핸들러
  const handleAnnouncementListClose = () => {
    setShowAnnouncementList(false);
    setAnnouncements([]);
  };

  // 공지사항 목록에서 특정 공지사항 클릭 핸들러
  const handleAnnouncementClick = (selectedAnnouncement: Announcement) => {
    setAnnouncement(selectedAnnouncement);
    setIsAutoPopup(false); // 목록에서 클릭한 공지사항 표시
    setShowAnnouncementList(false);
    setShowAnnouncementModal(true);
  };

  const handleNavigate = (page: 'home' | 'alarm' | 'admin' | 'assignments' | 'assignment-table' | 'assignment-card' | 'routine-detail' | 'points-table' | 'chat' | 'board' | 'license' | 'terms-of-service' | 'privacy-policy') => {
    // 현재 페이지가 과제 관리 페이지이고 변경사항이 있는지 확인
    const hasUnsavedChanges = 
      (currentPage === 'assignment-card' && assignmentCardHasChanges) ||
      (currentPage === 'assignment-table' && assignmentTableHasChanges);
    
    // 같은 페이지로 이동하는 경우는 그냥 진행
    if (page === currentPage) {
      return;
    }

    // 클래스챗 재생 중에는 다른 페이지로 이동 불가
    if (currentPage === 'chat' && page !== 'chat' && chatPlaying) {
      alert('재생 중에는 다른 페이지로 이동할 수 없습니다. 재생을 중지한 뒤 이동해 주세요.');
      return;
    }
    
    // 과제 관리 페이지에서 다른 페이지로 이동하려고 할 때 변경사항이 있으면 모달 표시
    if (hasUnsavedChanges && (currentPage === 'assignment-card' || currentPage === 'assignment-table')) {
      setPendingNavigation({ page, saveAndExit: false });
      return;
    }
    
    // 레거시 단독 과제관리 페이지는 더 이상 사용하지 않음
    if (page === 'assignment-table' || page === 'assignment-card') {
      setCurrentPage('assignments');
      return;
    }
    setCurrentPage(page as any);
  };
  
  // 저장하고 이동
  const handleSaveAndNavigate = async () => {
    if (!pendingNavigation) return;
    
    // 현재 페이지에 따라 저장 함수 호출
    if (currentPage === 'assignment-card' && assignmentCardSaveRef.current) {
      await assignmentCardSaveRef.current();
    } else if (currentPage === 'assignment-table' && assignmentTableSaveRef.current) {
      await assignmentTableSaveRef.current();
    }
    
    const { page } = pendingNavigation;
    setPendingNavigation(null);
    
    if (page === 'assignment-table' || page === 'assignment-card') {
      setCurrentPage('assignments');
      return;
    }
    setCurrentPage(page as any);
  };
  
  // 저장하지 않고 이동
  const handleNavigateWithoutSave = () => {
    if (!pendingNavigation) return;
    
    const { page } = pendingNavigation;
    setPendingNavigation(null);
    
    if (page === 'assignment-table' || page === 'assignment-card') {
      setCurrentPage('assignments');
      return;
    }
    setCurrentPage(page as any);
  };

  const handleNavigateToTable = () => {
    setCurrentPage('assignments');
    setShowAssignmentCardModal(false);
    setShowAssignmentTableModal(true);
  };

  const handleNavigateToCard = () => {
    setCurrentPage('assignments');
    setShowAssignmentTableModal(false);
    setShowAssignmentCardModal(true);
  };

  const handleBackToAssignments = () => {
    setShowAssignmentTableModal(false);
    setShowAssignmentCardModal(false);
    setCurrentPage('assignments');
  };

  const handleRequestCloseAssignmentModal = useCallback((type: 'table' | 'card') => {
    const hasChanges = type === 'table' ? assignmentTableHasChanges : assignmentCardHasChanges;
    if (hasChanges) {
      setPendingAssignmentModalClose(type);
      return;
    }
    if (type === 'table') {
      setShowAssignmentTableModal(false);
    } else {
      setShowAssignmentCardModal(false);
    }
  }, [assignmentCardHasChanges, assignmentTableHasChanges]);

  const handleCloseAssignmentModalWithoutSave = useCallback(() => {
    if (!pendingAssignmentModalClose) return;
    if (pendingAssignmentModalClose === 'table') {
      setShowAssignmentTableModal(false);
    } else {
      setShowAssignmentCardModal(false);
    }
    setPendingAssignmentModalClose(null);
  }, [pendingAssignmentModalClose]);

  /** 과제 관리 카드/테이블 닫기 경고: 배경 클릭 시 경고만 닫고 모달은 유지 */
  const handleDismissAssignmentCloseWarning = useCallback(() => {
    setPendingAssignmentModalClose(null);
  }, []);

  const handleSaveAndCloseAssignmentModal = useCallback(async () => {
    if (!pendingAssignmentModalClose) return;
    if (pendingAssignmentModalClose === 'table' && assignmentTableSaveRef.current) {
      await assignmentTableSaveRef.current();
      setShowAssignmentTableModal(false);
    } else if (pendingAssignmentModalClose === 'card' && assignmentCardSaveRef.current) {
      await assignmentCardSaveRef.current();
      setShowAssignmentCardModal(false);
    }
    setPendingAssignmentModalClose(null);
  }, [pendingAssignmentModalClose]);

  const handleUpdateUser = async (updatedUser: User) => {
    console.log('🔄 handleUpdateUser 호출:', {
      userId: updatedUser.id,
      currentClassId: updatedUser.currentClassId
    });
    
    setUser(updatedUser);
    
    try {
      // Firestore에 업데이트된 사용자 데이터 저장
      await userDataService.saveUserData(updatedUser, alarmSets);
    } catch (error) {
      console.error('사용자 데이터 업데이트 실패:', error);
    }
    
    // 로컬 스토리지에도 백업 저장
    saveUserCustomData(updatedUser);
  };

  const handleProfileModalOpen = () => {
    console.log('🔍 App: handleProfileModalOpen 호출됨');
    console.log('🔍 App: showProfileModal을 true로 설정');
    setShowProfileModal(true);
  };

  const handleProfileModalClose = () => {
    setShowProfileModal(false);
  };

  const handleLicensePageNavigate = () => {
    setCurrentPage('license');
  };

  const handleClassManageModalOpen = () => {
    setShowClassManageModal(true);
  };

  /** 현재 선택된 클래스의 클래스 설정 모달을 바로 열기 (클래스 관리 모달 없이) */
  const handleOpenClassSettingsDirect = useCallback(async () => {
    if (!user?.id || !currentClass) return;
    try {
      const latest = await userDataService.loadClass(user.id, currentClass.id);
      setClassForSettingsDirect(latest || currentClass);
    } catch {
      setClassForSettingsDirect(currentClass);
    }
    setShowClassSettingsDirect(true);
  }, [user?.id, currentClass]);

  const handleClassManageModalClose = () => {
    setShowClassManageModal(false);
  };

  // 알람 페이지 모달 핸들러들
  const handleNewAlarmModalOpen = () => {
    setShowNewAlarmModal(true);
  };

  const handleNewAlarmModalClose = () => {
    setShowNewAlarmModal(false);
  };

  // handleSetNameModalOpen, handleSetNameModalClose 제거 - 사용되지 않음

  const handleEditSetNameModalOpen = () => {
    setShowEditSetNameModal(true);
  };

  const handleEditSetNameModalClose = () => {
    setShowEditSetNameModal(false);
  };


  // 알람 시간이 지났는지 확인하는 함수 (알람 시간이 되기 전까지는 종 아이콘 표시)
  const isAlarmTimePassed = (alarmTime: string): boolean => {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes(); // 현재 시간을 분으로 변환
    
    const [hours, minutes] = alarmTime.split(':').map(Number);
    const alarmTimeInMinutes = hours * 60 + minutes; // 알람 시간을 분으로 변환
    
    return currentTime >= alarmTimeInMinutes; // 알람 시간이 되었거나 지났을 때 true 반환
  };

  const handleAlarmPopupClose = () => {
    setShowAlarmPopup(false);
    
    // 디버깅을 위한 로그
    console.log('handleAlarmPopupClose 호출됨');
    console.log('currentAlarmInfo:', currentAlarmInfo);
    if (currentAlarmInfo) {
      console.log('알람 시간:', currentAlarmInfo.time);
      console.log('isAlarmTimePassed 결과:', isAlarmTimePassed(currentAlarmInfo.time));
      
      // 사용자가 닫은 알람을 추적 (같은 알람의 다른 타이밍에서도 다시 열리지 않도록)
      const alarmKey = `${currentAlarmInfo.time}-${currentAlarmInfo.type}`;
      setDismissedAlarms(prev => new Set(prev).add(alarmKey));
      console.log(`🔕 알람 팝업 닫힘 상태 저장:`, alarmKey);
    }
    
    // 알람 시간이 되었거나 지났으면 currentAlarmInfo를 null로 설정하여 종 아이콘을 숨김
    if (currentAlarmInfo && isAlarmTimePassed(currentAlarmInfo.time)) {
      console.log('알람 시간이 지나서 currentAlarmInfo를 null로 설정');
      setCurrentAlarmInfo(null);
      // 알람 시간이 지났으면 dismissedAlarms에서도 제거 (다음 날 같은 알람이 다시 울릴 수 있도록)
      const alarmKey = `${currentAlarmInfo.time}-${currentAlarmInfo.type}`;
      setDismissedAlarms(prev => {
        const newSet = new Set(prev);
        newSet.delete(alarmKey);
        return newSet;
      });
    } else {
      console.log('알람 시간이 지나지 않아서 currentAlarmInfo 유지');
    }
    // 알람 시간이 되지 않았으면 currentAlarmInfo를 유지하여 종 아이콘 표시
  };


  // FloatingActionButton 위치 계산 및 종 아이콘 위치 설정
  useEffect(() => {
    const calculateBellPosition = () => {
      // 모든 버튼 요소 찾기
      const allButtons = document.querySelectorAll('button');
      let fabButton: HTMLButtonElement | null = null;
      
      for (let i = 0; i < allButtons.length; i++) {
        const btn = allButtons[i];
        const style = window.getComputedStyle(btn);
        const rect = btn.getBoundingClientRect();
        
        // FloatingActionButton 특성 확인: 원형, 우측 하단, + 아이콘 포함
        if (
          style.borderRadius === '50%' &&
          style.position === 'fixed' &&
          rect.bottom > window.innerHeight - 200 && // 하단 근처
          rect.right > window.innerWidth - 100 && // 우측 근처
          (btn.textContent?.trim() === '+' || btn.querySelector('svg'))
        ) {
          fabButton = btn as HTMLButtonElement;
          break;
        }
      }
      
      const isMobile = window.innerWidth <= 768;
      let bottom: number, right: number;
      
      if (fabButton) {
        const rect = fabButton.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(fabButton);
        
        // FloatingActionButton 위에 배치 (버튼 높이 + 간격 12px)
        // bottom은 하단에서의 거리이므로: FloatingActionButton의 bottom + 버튼 높이 + 간격
        const fabBottom = window.innerHeight - rect.bottom;
        bottom = fabBottom + rect.height + 12;
        
        // FloatingActionButton과 정확히 같은 right 값 사용
        // computedStyle.right을 파싱하여 px 값을 추출
        const fabRight = parseFloat(computedStyle.right) || (window.innerWidth - rect.right);
        right = fabRight;
      } else {
        // FloatingActionButton이 없는 경우, FloatingActionButton이 있던 위치 사용
        const safeAreaBottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-bottom)')) || 0;
        bottom = isMobile ? safeAreaBottom + 80 : safeAreaBottom + 60;
        right = isMobile ? 20 : 24;
      }
      
      // 값이 실제로 변경될 때만 상태 업데이트 (무한 루프 방지)
      setBellIconPosition(prev => {
        if (prev && prev.bottom === bottom && prev.right === right) {
          return prev; // 변경 없음
        }
        return { bottom, right };
      });
    };
    
    // 초기 계산
    calculateBellPosition();
    
    // 리사이즈 감지만 사용 (MutationObserver 제거하여 무한 루프 방지)
    window.addEventListener('resize', calculateBellPosition);
    
    return () => {
      window.removeEventListener('resize', calculateBellPosition);
    };
  }, [currentPage, showAlarmPopup]); // currentPage와 showAlarmPopup 변경 시 재계산


  // 알람 끄기 함수 - 전역 알람 서비스 사용
  const handleStopAlarm = () => {
    // 전역 알람 서비스의 stopAlarm 함수 호출
    if (stopAlarm) {
      stopAlarm();
    }
    
    // 모든 알람 소리 정지
    const { stopAllAlarmSounds } = require('./utils/alarmUtils');
    stopAllAlarmSounds();

    // 같은 시각·타입의 남은 리마인더(3M: 2분전·1분전 등)도 울리지 않도록 (팝업닫기와 동일 키)
    if (currentAlarmInfo) {
      const alarmKey = `${currentAlarmInfo.time}-${currentAlarmInfo.type}`;
      setDismissedAlarms(prev => new Set(prev).add(alarmKey));
    }
    
    setShowAlarmPopup(false);
    setCurrentAlarmInfo(null);
  };

  const isPresentationRoute =
    typeof window !== 'undefined' && window.location.pathname === '/presentation';
  const isStudentChatRoute =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/member-page');

  // auth 확인 완료 후, 로그인된 사용자지만 currentClass가 없으면 저장된 클래스 ID로 복원
  useEffect(() => {
    if (!authChecked) return;
    if (!user) return;
    if (currentClass) return;

    const classId = user.currentClassId || loadCurrentClassId(user.id);
    if (!classId) return;

    loadClassData(user.id, classId);
  }, [authChecked, user, currentClass, loadClassData]);

  if (isPresentationRoute) {
    return <PresentationPage />;
  }

  // 학생 멤버 전용 페이지는 코디 로그인 세션과 무관하게 항상 렌더
  if (isStudentChatRoute) {
    return (
      <Router>
        <Routes>
          <Route path="/member-page" element={<StudentChatPage />} />
          <Route path="/*" element={<StudentChatPage />} />
        </Routes>
        <GlobalToast />
      </Router>
    );
  }

  // 인증 상태 확인 전에는 로딩 화면 표시
  if (!authChecked) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--color-bg, #141414)',
        color: 'var(--color-text-primary, #f5f5f5)'
      }}>
        <div>로딩 중...</div>
      </div>
    );
  }

  return (
    <Router>
    <div className="App" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {user ? (
        <>
          {/* 상단 헤더 영역 */}
          <header style={{ flexShrink: 0 }}>
            {currentPage === 'home' ? (
              <HomePage 
                key={`home_${currentClass?.id || 'no-class'}_${currentMemberGroup}`}
                onNavigate={handleNavigate} 
                user={user} 
                onLogout={handleLogout} 
                onUpdateUser={handleUpdateUser}
                classes={classes}
                currentClass={currentClass}
                memberGroups={availableMemberGroups}
                currentMemberGroup={currentMemberGroup}
                onMemberGroupChange={handleMemberGroupChange}
                onClassManageModalOpen={handleClassManageModalOpen}
                onClassSettingsDirectClick={handleOpenClassSettingsDirect}
                onSeatAssignmentClick={handleSeatAssignmentClick}
                onLevelSettingsClick={handleLevelSettingsClick}
                onPointManagementClick={handlePointManagementClick}
                onItemCardSettingsClick={handleItemCardSettingsClick}
                onMemberProfileSettingsClick={handleMemberProfileSettingsOpen}
                onAssignmentSettingsClick={handleAssignmentSettingsClick}
                onProfileModalOpen={handleProfileModalOpen}
                themeMode={themeMode}
                onToggleTheme={handleThemeToggle}
                onAnnouncementOpen={handleAnnouncementListOpen}
              />
            ) : currentPage === 'admin' ? (
              <AdminPage user={user} onBack={() => setCurrentPage('home')} />
            ) : currentPage === 'assignments' ? (
            <>
              <Header 
                user={user} 
                onLogout={handleLogout} 
                onNavigate={handleNavigate}
                currentPage="assignments"
                onUpdateUser={handleUpdateUser}
                onProfileModalOpen={handleProfileModalOpen}
                onClassManageModalOpen={handleClassManageModalOpen}
                classes={classes}
                currentClass={currentClass}
                memberGroups={availableMemberGroups}
                currentMemberGroup={currentMemberGroup}
                onMemberGroupChange={handleMemberGroupChange}
                themeMode={themeMode}
                onToggleTheme={handleThemeToggle}
                onSeatAssignmentClick={handleSeatAssignmentClick}
                onLevelSettingsClick={handleLevelSettingsClick}
                onPointManagementClick={handlePointManagementClick}
                onItemCardSettingsClick={handleItemCardSettingsClick}
                onAssignmentSettingsClick={handleAssignmentSettingsClick}
                onMemberProfileSettingsClick={handleMemberProfileSettingsOpen}
                onDiceModalOpen={handleDiceModalOpen}
                onUpgradeModalOpen={handleLicensePageNavigate}
              />
              <AssignmentsPage
                key={`assignments_${currentClass?.id || 'no-class'}_${currentMemberGroup}`}
                user={user}
                onLogout={handleLogout}
                onUpdateUser={handleUpdateUser}
                onClassManageModalOpen={handleClassManageModalOpen}
                classes={classes}
                currentClass={currentClass}
                memberGroups={availableMemberGroups}
                currentMemberGroup={currentMemberGroup}
                onMemberGroupChange={handleMemberGroupChange}
                onNavigate={handleNavigate}
                onMembersChange={handleMembersChange}
                onRoutineDetail={(routineId: string) => {
                  setSelectedRoutineId(routineId);
                  setCurrentPage('routine-detail');
                }}
                onNavigateToTable={handleNavigateToTable}
                onNavigateToCard={handleNavigateToCard}
                onSeatAssignmentClick={handleSeatAssignmentClick}
                onLicenseClick={handleLicensePageNavigate}
                onLevelSettingsClick={handleLevelSettingsClick}
                onPointManagementClick={handlePointManagementClick}
                onItemCardSettingsClick={handleItemCardSettingsClick}
                onAssignmentSettingsClick={handleAssignmentSettingsClick}
                themeMode={themeMode}
                onToggleTheme={handleThemeToggle}
                onUpgradeModalOpen={handleLicensePageNavigate}
              />
            </>
          ) : currentPage === 'routine-detail' ? (
            <>
              <Header 
                user={user} 
                onLogout={handleLogout} 
                onNavigate={handleNavigate}
                currentPage="assignments"
                onUpdateUser={handleUpdateUser}
                onProfileModalOpen={handleProfileModalOpen}
                onClassManageModalOpen={handleClassManageModalOpen}
                classes={classes}
                currentClass={currentClass}
                memberGroups={availableMemberGroups}
                currentMemberGroup={currentMemberGroup}
                onMemberGroupChange={handleMemberGroupChange}
                themeMode={themeMode}
                onToggleTheme={handleThemeToggle}
                onMemberProfileSettingsClick={handleMemberProfileSettingsOpen}
                onDiceModalOpen={handleDiceModalOpen}
              />
              <RoutineAssignmentDetailPage
                user={user}
                currentClass={currentClass}
                routineId={selectedRoutineId}
                onBack={() => setCurrentPage('assignments')}
              />
            </>
          ) : currentPage === 'points-table' ? (
            <>
              <Header
                user={user}
                onLogout={handleLogout}
                onNavigate={handleNavigate}
                currentPage="points-table"
                onUpdateUser={handleUpdateUser}
                onProfileModalOpen={handleProfileModalOpen}
                onClassManageModalOpen={handleClassManageModalOpen}
                classes={classes}
                currentClass={currentClass}
                memberGroups={availableMemberGroups}
                currentMemberGroup={currentMemberGroup}
                onMemberGroupChange={handleMemberGroupChange}
                themeMode={themeMode}
                onToggleTheme={handleThemeToggle}
                onSeatAssignmentClick={handleSeatAssignmentClick}
                onLevelSettingsClick={handleLevelSettingsClick}
                onPointManagementClick={handlePointManagementClick}
                onItemCardSettingsClick={handleItemCardSettingsClick}
                onAssignmentSettingsClick={handleAssignmentSettingsClick}
                onMemberProfileSettingsClick={handleMemberProfileSettingsOpen}
                onDiceModalOpen={handleDiceModalOpen}
                onUpgradeModalOpen={handleLicensePageNavigate}
              />
              <PointsTablePage
                key={`points_${currentClass?.id || 'no-class'}_${currentMemberGroup}`}
                user={user}
                currentClass={currentClass}
                currentMemberGroup={currentMemberGroup}
                onBack={() => setCurrentPage('assignments')}
                onLogout={handleLogout}
                onNavigate={handleNavigate}
                onUpdateUser={handleUpdateUser}
                onProfileModalOpen={handleProfileModalOpen}
                onClassManageModalOpen={handleClassManageModalOpen}
                classes={classes}
                onSeatAssignmentClick={handleSeatAssignmentClick}
                onLevelSettingsClick={handleLevelSettingsClick}
                onPointManagementClick={handlePointManagementClick}
                onItemCardSettingsClick={handleItemCardSettingsClick}
                onAssignmentSettingsClick={handleAssignmentSettingsClick}
                onHomeNavigate={() => setCurrentPage('home')}
                onAlarmNavigate={() => setCurrentPage('alarm')}
                ref={pointsTablePageRef}
                showSeatAssignmentModal={showSeatAssignmentModal}
                setShowSeatAssignmentModal={setShowSeatAssignmentModal}
                showLevelSettingsModal={showLevelSettingsModal}
                setShowLevelSettingsModal={setShowLevelSettingsModal}
                showPointManagementModal={showPointManagementModal}
                setShowPointManagementModal={setShowPointManagementModal}
                showItemCardSettingsModal={showItemCardSettingsModal}
                setShowItemCardSettingsModal={setShowItemCardSettingsModal}
                onLicenseClick={handleLicensePageNavigate}
              />
            </>
          ) : currentPage === 'chat' ? (
            <>
              <Header
                user={user}
                onLogout={handleLogout}
                onNavigate={handleNavigate}
                currentPage="chat"
                onUpdateUser={handleUpdateUser}
                onProfileModalOpen={handleProfileModalOpen}
                onClassManageModalOpen={handleClassManageModalOpen}
                classes={classes}
                currentClass={currentClass}
                memberGroups={availableMemberGroups}
                currentMemberGroup={currentMemberGroup}
                onMemberGroupChange={handleMemberGroupChange}
                themeMode={themeMode}
                onToggleTheme={handleThemeToggle}
                onMemberProfileSettingsClick={handleMemberProfileSettingsOpen}
                onDiceModalOpen={handleDiceModalOpen}
                onUpgradeModalOpen={handleLicensePageNavigate}
              />
              <ChatPage 
                key={`chat_${currentClass?.id || 'no-class'}_${currentMemberGroup}`}
                user={user}
                currentClass={currentClass}
                onNavigate={handleNavigate}
                onChatPlayingChange={setChatPlaying}
                onLogout={handleLogout}
                onUpdateUser={handleUpdateUser}
                onProfileModalOpen={handleProfileModalOpen}
                onClassManageModalOpen={handleClassManageModalOpen}
                classes={classes}
                onLicenseClick={handleLicensePageNavigate}
                themeMode={themeMode}
                onToggleTheme={handleThemeToggle}
                onSeatAssignmentClick={handleSeatAssignmentClick}
                onLevelSettingsClick={handleLevelSettingsClick}
                onPointManagementClick={handlePointManagementClick}
                onItemCardSettingsClick={handleItemCardSettingsClick}
                onAssignmentSettingsClick={handleAssignmentSettingsClick}
                memberGroups={availableMemberGroups}
                currentMemberGroup={currentMemberGroup}
                onMemberGroupChange={handleMemberGroupChange}
              />
            </>
          ) : currentPage === 'board' ? (
            <>
              <Header
                user={user}
                onLogout={handleLogout}
                onNavigate={handleNavigate}
                currentPage="board"
                onUpdateUser={handleUpdateUser}
                onProfileModalOpen={handleProfileModalOpen}
                onClassManageModalOpen={handleClassManageModalOpen}
                classes={classes}
                currentClass={currentClass}
                memberGroups={availableMemberGroups}
                currentMemberGroup={currentMemberGroup}
                onMemberGroupChange={handleMemberGroupChange}
                themeMode={themeMode}
                onToggleTheme={handleThemeToggle}
                onSeatAssignmentClick={handleSeatAssignmentClick}
                onLevelSettingsClick={handleLevelSettingsClick}
                onPointManagementClick={handlePointManagementClick}
                onItemCardSettingsClick={handleItemCardSettingsClick}
                onAssignmentSettingsClick={handleAssignmentSettingsClick}
                onMemberProfileSettingsClick={handleMemberProfileSettingsOpen}
                onDiceModalOpen={handleDiceModalOpen}
                onUpgradeModalOpen={handleLicensePageNavigate}
              />
              <BoardPage 
                key={`board_${currentClass?.id || 'no-class'}_${currentMemberGroup}`}
                user={user} 
                currentClass={currentClass}
                currentMemberGroup={currentMemberGroup}
                onTimerModalOpen={() => {
                  setShowTimerModal(true);
                  setTimerModalHidden(false);
                }}
                onDiceModalOpen={handleDiceModalOpen}
                onLicenseClick={handleLicensePageNavigate}
              />
            </>
          ) : currentPage === 'license' ? (
            <LicensePage
              user={user}
              onLogout={handleLogout}
              onNavigate={handleNavigate}
              onUpdateUser={handleUpdateUser}
              onProfileModalOpen={handleProfileModalOpen}
              onClassManageModalOpen={handleClassManageModalOpen}
              classes={classes}
              currentClass={currentClass}
              memberGroups={availableMemberGroups}
              currentMemberGroup={currentMemberGroup}
              onMemberGroupChange={handleMemberGroupChange}
              themeMode={themeMode}
              onToggleTheme={handleThemeToggle}
              onSeatAssignmentClick={handleSeatAssignmentClick}
              onLevelSettingsClick={handleLevelSettingsClick}
              onPointManagementClick={handlePointManagementClick}
              onItemCardSettingsClick={handleItemCardSettingsClick}
              onAssignmentSettingsClick={handleAssignmentSettingsClick}
            />
          ) : currentPage === 'terms-of-service' ? (
            <TermsOfServicePage
              user={user}
              onLogout={handleLogout}
              onNavigate={handleNavigate}
              onUpdateUser={handleUpdateUser}
              onProfileModalOpen={handleProfileModalOpen}
              onClassManageModalOpen={handleClassManageModalOpen}
              classes={classes}
              currentClass={currentClass}
              memberGroups={availableMemberGroups}
              currentMemberGroup={currentMemberGroup}
              onMemberGroupChange={handleMemberGroupChange}
              themeMode={themeMode}
              onToggleTheme={handleThemeToggle}
              onSeatAssignmentClick={handleSeatAssignmentClick}
              onLevelSettingsClick={handleLevelSettingsClick}
              onPointManagementClick={handlePointManagementClick}
              onItemCardSettingsClick={handleItemCardSettingsClick}
              onAssignmentSettingsClick={handleAssignmentSettingsClick}
              onLicenseClick={handleLicensePageNavigate}
            />
          ) : currentPage === 'privacy-policy' ? (
            <PrivacyPolicyPage
              user={user}
              onLogout={handleLogout}
              onNavigate={handleNavigate}
              onUpdateUser={handleUpdateUser}
              onProfileModalOpen={handleProfileModalOpen}
              onClassManageModalOpen={handleClassManageModalOpen}
              classes={classes}
              currentClass={currentClass}
              memberGroups={availableMemberGroups}
              currentMemberGroup={currentMemberGroup}
              onMemberGroupChange={handleMemberGroupChange}
              themeMode={themeMode}
              onToggleTheme={handleThemeToggle}
              onSeatAssignmentClick={handleSeatAssignmentClick}
              onLevelSettingsClick={handleLevelSettingsClick}
              onPointManagementClick={handlePointManagementClick}
              onItemCardSettingsClick={handleItemCardSettingsClick}
              onAssignmentSettingsClick={handleAssignmentSettingsClick}
              onLicenseClick={handleLicensePageNavigate}
            />
          ) : (
            <>
              <Header 
                user={user} 
                onLogout={handleLogout} 
                onNavigate={handleNavigate}
                currentPage={currentPage === 'assignment-table' || currentPage === 'assignment-card' ? 'assignments' : currentPage}
                onUpdateUser={handleUpdateUser}
                onProfileModalOpen={handleProfileModalOpen}
                onClassManageModalOpen={handleClassManageModalOpen}
                classes={classes}
                currentClass={currentClass}
                memberGroups={availableMemberGroups}
                currentMemberGroup={currentMemberGroup}
                onMemberGroupChange={handleMemberGroupChange}
                themeMode={themeMode}
                onToggleTheme={handleThemeToggle}
                onSeatAssignmentClick={handleSeatAssignmentClick}
                onLevelSettingsClick={handleLevelSettingsClick}
                onPointManagementClick={handlePointManagementClick}
                onItemCardSettingsClick={handleItemCardSettingsClick}
                onAssignmentSettingsClick={handleAssignmentSettingsClick}
                onMemberProfileSettingsClick={handleMemberProfileSettingsOpen}
                onDiceModalOpen={handleDiceModalOpen}
                onUpgradeModalOpen={handleLicensePageNavigate}
              />
                <AlarmPage
                  key={`alarm_${currentClass?.id || 'no-class'}_${currentMemberGroup}`}
                  user={user}
                  onLogout={handleLogout}
                  onNavigate={handleNavigate}
                  onUpdateUser={handleUpdateUser}
                  onProfileModalOpen={handleProfileModalOpen}
                  onClassManageModalOpen={handleClassManageModalOpen}
                  classes={classes}
                  currentClass={currentClass}
                  memberGroups={availableMemberGroups}
                  currentMemberGroup={currentMemberGroup}
                  onMemberGroupChange={handleMemberGroupChange}
                  onSeatAssignmentClick={handleSeatAssignmentClick}
                  onLevelSettingsClick={handleLevelSettingsClick}
                  onPointManagementClick={handlePointManagementClick}
                  onItemCardSettingsClick={handleItemCardSettingsClick}
                  onAssignmentSettingsClick={handleAssignmentSettingsClick}
                  onNewAlarmModalOpen={handleNewAlarmModalOpen}
                  onSetNameModalOpen={() => {}} // 빈 함수로 대체 - 사용되지 않음
                  onEditSetNameModalOpen={handleEditSetNameModalOpen}
                  onAlarmPopupOpen={handleAlarmPopupOpen}
                  onAlarmPopupClose={handleAlarmPopupClose}
                  timeFormat={timeFormat}
                  setTimeFormat={setTimeFormat}
                  onCloneSetSave={(newName: string) => {
                    // 복제 저장 로직은 AlarmPage에서 처리
                    console.log('App: 복제 저장 콜백 호출됨:', newName);
                  }}
                  // 전역 알람 서비스 데이터 전달
                  alarmSets={alarmSets}
                  setAlarmSets={setAlarmSets}
                  loadUserAlarmData={loadUserAlarmData}
                  themeMode={themeMode}
                  onToggleTheme={handleThemeToggle}
                  onLicenseClick={handleLicensePageNavigate}
                />
            </>
          )}
          </header>
          
          {/* 메인 콘텐츠 영역 */}
          <main style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* 페이지별 콘텐츠는 이미 header에 포함되어 있음 */}
          </main>
        </>
      ) : (
        <Routes>
          <Route path="/presentation" element={<PresentationPage />} />
          <Route path="/member-page" element={<StudentChatPage />} />
          <Route path="/*" element={<LoginPage onLogin={handleLogin} themeMode={themeMode} onToggleTheme={handleThemeToggle} />} />
        </Routes>
      )}
    </div>
      
      {/* 하단 네비바 - 고정 위치 (홈페이지 제외) */}
      {user &&
        currentPage !== 'home' &&
        currentPage !== 'assignment-table' &&
        currentPage !== 'assignment-card' &&
        !(currentPage === 'assignments' && (showAssignmentTableModal || showAssignmentCardModal)) && (
        <BottomNavigation 
          currentPage={currentPage}
          onNavigate={handleNavigate}
        />
      )}
      
      {/* 네비게이션 경고 모달 */}
      {pendingNavigation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }} onClick={handleNavigateWithoutSave}>
          <div style={{
            background: 'var(--color-surface)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 600,
              margin: '0 0 16px 0',
              color: 'var(--color-text-primary)'
            }}>변경된 데이터가 있습니다.</h3>
            <p style={{
              fontSize: '14px',
              color: 'var(--color-text-gray)',
              margin: '0 0 24px 0',
              lineHeight: 1.5
            }}>저장하지 않고 나가겠습니까?</p>
            <div style={{
              display: 'flex',
              gap: '8px',
              width: '100%'
            }}>
              <button
                onClick={handleNavigateWithoutSave}
                style={{
                  flex: 1,
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  background: 'var(--color-surface-highlight)',
                  color: 'var(--color-text-primary)',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'var(--color-border)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'var(--color-surface-highlight)'}
              >
                저장하지 않고 이동
              </button>
              <button
                onClick={handleSaveAndNavigate}
                style={{
                  flex: 1,
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  background: 'var(--color-accent-strong)',
                  color: '#fff',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'var(--color-accent)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'var(--color-accent-strong)'}
              >
                저장하고 이동
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 모달들을 App div 밖에서 독립적으로 렌더링 */}
      {user && (
        <>
          {currentPage === 'assignments' && showAssignmentTableModal && (
            <div
              onClick={() => handleRequestCloseAssignmentModal('table')}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 12000,
                background: 'rgba(0, 0, 0, 0.45)',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                paddingTop: 'max(16px, env(safe-area-inset-top, 0px))',
                paddingBottom: 16,
                paddingLeft: 16,
                paddingRight: 16,
                overflowY: 'auto',
                overflowX: 'hidden',
                overscrollBehavior: 'contain'
              }}
            >
              <div
                style={{
                  width: 'min(1560px, calc(100vw - 24px))',
                  minHeight: 0,
                  height: ASSIGNMENT_EMBEDDED_MODAL_HEIGHT,
                  maxHeight: ASSIGNMENT_EMBEDDED_MODAL_HEIGHT,
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'var(--color-surface)',
                  borderRadius: '16px',
                  border: 'var(--modal-shell-border)',
                  overflowY: 'hidden',
                  overflowX: 'hidden',
                  boxShadow: 'var(--modal-shadow-xlarge)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <AssignmentTablePageFinal
                  key={`assignment-table-modal-${currentClass?.id || 'no-class'}-${currentMemberGroup}`}
                  userId={user?.id || ''}
                  classId={user?.currentClassId || ''}
                  onBack={() => handleRequestCloseAssignmentModal('table')}
                  user={user}
                  currentClass={currentClass}
                  currentMemberGroup={currentMemberGroup}
                  onHasChangesChange={setAssignmentTableHasChanges}
                  onSaveAndExit={async () => {}}
                  onSaveRef={(saveFn) => {
                    assignmentTableSaveRef.current = saveFn;
                  }}
                  embeddedInModal
                />
              </div>
            </div>
          )}

          {currentPage === 'assignments' && showAssignmentCardModal && (
            <div
              onClick={() => handleRequestCloseAssignmentModal('card')}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 12000,
                background: 'rgba(0, 0, 0, 0.45)',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                paddingTop: 'max(16px, env(safe-area-inset-top, 0px))',
                paddingBottom: 16,
                paddingLeft: 16,
                paddingRight: 16,
                overflowY: 'auto',
                overflowX: 'hidden',
                overscrollBehavior: 'contain'
              }}
            >
              <div
                style={{
                  width: 'min(1560px, calc(100vw - 24px))',
                  minHeight: 0,
                  height: ASSIGNMENT_EMBEDDED_MODAL_HEIGHT,
                  maxHeight: ASSIGNMENT_EMBEDDED_MODAL_HEIGHT,
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'var(--color-surface)',
                  borderRadius: '16px',
                  border: 'var(--modal-shell-border)',
                  overflowY: 'hidden',
                  overflowX: 'hidden',
                  boxShadow: 'var(--modal-shadow-xlarge)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <AssignmentCardPage
                  key={`assignment-card-modal-${currentClass?.id || 'no-class'}-${currentMemberGroup}`}
                  userId={user?.id || ''}
                  classId={user?.currentClassId || ''}
                  onBack={() => handleRequestCloseAssignmentModal('card')}
                  user={user}
                  currentClass={currentClass}
                  currentMemberGroup={currentMemberGroup}
                  onHasChangesChange={setAssignmentCardHasChanges}
                  onSaveAndExit={async () => {}}
                  onSaveRef={(saveFn) => {
                    assignmentCardSaveRef.current = saveFn;
                  }}
                  embeddedInModal
                />
              </div>
            </div>
          )}

          <ProfileEditModal
            isOpen={showProfileModal}
            user={user}
            onSave={handleUpdateUser}
            onCancel={handleProfileModalClose}
            onLogout={handleLogout}
          />

          
          <ClassManageModal
            isOpen={showClassManageModal}
            onClose={handleClassManageModalClose}
            userId={user.id}
            user={user}
            currentMemberGroup={currentMemberGroup}
            initialClassToOpenSettings={initialClassToOpenSettingsForModal}
            onInitialClassSettingsOpened={() => setInitialClassToOpenSettingsForModal(null)}
            onMembersChange={handleMembersChange}
            onLicenseClick={handleLicensePageNavigate}
            onClassReset={() => {
              // 클래스 초기화 시 캐시 데이터 리셋 (기본값으로 설정하여 다음 로드 시 서버에서 가져오게 함)
              console.log('🔄 클래스 초기화로 인한 캐시 리셋');
              setAssignmentDefaultPoints(10);
              setAssignmentStatusDefaultPoints({
                pending: 0,
                confirmed: 0,
                delayed: 0,
                exempted: 0
              });
            }}
            onClassChange={async (classChangeInfo) => {
              if (classChangeInfo?.currentClassId) {
                // 클래스 선택 변경
                console.log('🔄 클래스 선택 변경 후 사용자 정보 업데이트:', {
                  newCurrentClassId: classChangeInfo.currentClassId,
                  keepModalOpen: classChangeInfo.keepModalOpen
                });
                
                // currentClassId만 업데이트
                const updatedUser = { ...user, currentClassId: classChangeInfo.currentClassId };
                setUser(updatedUser);
                
                // 현재 클래스도 업데이트 - 클래스 목록에서 찾을 수 없으면 다시 로드
                let foundClass = classes.find(cls => cls.id === classChangeInfo.currentClassId);
                
                if (!foundClass) {
                  // 새로 생성된 클래스일 수 있으므로 클래스 목록 다시 로드
                  console.log('🔄 클래스를 찾을 수 없어서 클래스 목록 다시 로드');
                  const userClasses = await userDataService.loadUserClasses(user.id);
                  setClasses(userClasses);
                  foundClass = userClasses.find(cls => cls.id === classChangeInfo.currentClassId);
                }
                
                setCurrentClass(foundClass || null);
                
                // Firebase에는 currentClassId만 저장 (클래스 생성/삭제 시에는 이미 저장됨)
                if (!classChangeInfo.keepModalOpen) {
                  userDataService.saveCurrentClassId(user.id, classChangeInfo.currentClassId);
                }
                
                // 로컬 스토리지에도 백업
                saveUserCustomData(updatedUser);
                
                // keepModalOpen 플래그가 없을 때만 모달 닫기 및 홈 페이지로 이동
                if (!classChangeInfo.keepModalOpen) {
                  handleClassManageModalClose();
                  
                  // 홈 페이지로 이동하여 데이터 새로고침
                  setCurrentPage('home');
                }
              } else {
                // 강제 업데이트 (클래스 이름 변경 등) - 모달은 닫지 않음
                console.log('🔄 클래스 데이터 변경 후 다시 로드 (모달 유지)');
                await loadClassData(user.id, user.currentClassId);
                
                // 강제 리렌더링을 위해 user 상태도 업데이트
                setUser(prevUser => prevUser ? { ...prevUser } : null);
              }
            }}
          />
        </>
      )}

      {/* 포인트 페이지 전역 모달들 - 모든 페이지에서 접근 가능 */}
      {user && currentClass && (
        <>
          <SeatAssignmentModal
            isOpen={showSeatAssignmentModal}
            onClose={() => {
              console.log('🔍 자리 배정 모달 닫힘');
              setShowSeatAssignmentModal(false);
            }}
            members={classMembers}
            userId={user.id}
            classId={currentClass.id}
            memberGroup={currentMemberGroup}
            onSave={async (updatedMembers) => {
              console.log('🔍 자리 배정 저장:', { updatedMembersCount: updatedMembers.length });
              setClassMembers(updatedMembers);
              setShowSeatAssignmentModal(false);
            }}
            onCopy={async (updatedMembers) => {
              console.log('🔍 자리 배정 복사:', { updatedMembersCount: updatedMembers.length });
              setClassMembers(updatedMembers);
              // 복사 후에는 모달을 닫지 않음
            }}
          />


          <ItemCardSettingsModal
            isOpen={showItemCardSettingsModal}
            onClose={() => {
              console.log('🔍 아이템 카드 설정 모달 닫힘');
              setShowItemCardSettingsModal(false);
            }}
            userId={user.id}
            classId={currentClass.id}
            user={user}
            onLicenseClick={handleLicensePageNavigate}
          />

          <PointManagementModal
            isOpen={showPointManagementModal}
            onClose={() => {
              console.log('🔍 포인트 세트 설정 모달 닫힘');
              setShowPointManagementModal(false);
            }}
            userId={user.id}
            classId={currentClass.id}
            defaultPointValue={1}
            initialTab={pointManagementModalInitialTab}
          />
        </>
      )}

      {/* 클래스 설정 모달만 바로 열기 (클래스 관리 모달 없이) */}
      {user && classForSettingsDirect && showClassSettingsDirect && (
        <ClassSettingsModal
          isOpen={showClassSettingsDirect}
          classData={classForSettingsDirect}
          userId={user.id}
          user={user}
          currentMemberGroup={currentMemberGroup}
          onClose={() => {
            setShowClassSettingsDirect(false);
            setClassForSettingsDirect(null);
          }}
          onSave={async (updatedClass) => {
            await userDataService.saveClass(user.id, updatedClass);
            if (currentClass?.id === updatedClass.id) {
              await loadClassData(user.id, updatedClass.id);
              setUser(prev => (prev ? { ...prev } : null));
            }
            handleMembersChange();
          }}
          getExistingClassCodes={async () => {
            try {
              return await userDataService.getAllClassCodes();
            } catch {
              const classes = await userDataService.loadUserClasses(user.id);
              return classes.map(c => c.classCode).filter((code): code is string => !!code);
            }
          }}
          onMembersChange={handleMembersChange}
          onClassReset={() => {
            setAssignmentDefaultPoints(10);
            setAssignmentStatusDefaultPoints({
              pending: 0,
              confirmed: 0,
              delayed: 0,
              exempted: 0
            });
          }}
          onLicenseClick={handleLicensePageNavigate}
        />
      )}
      {/* 레벨 설정 모달 - 전역 모달 */}
      {user && currentClass && (
        <LevelSettingsModal
          isOpen={showLevelSettingsModal}
          onClose={() => {
            console.log('🔍 레벨 설정 모달 닫힘');
            setShowLevelSettingsModal(false);
          }}
          userId={user.id}
          classId={currentClass.id}
        />
      )}

      {/* 과제 기본 환경설정 모달 - 전역 모달 */}
      {user && currentClass && (
        <AssignmentSettingsModal
          isOpen={showAssignmentSettingsModal}
          onClose={() => {
            console.log('🔍 과제 기본 환경설정 모달 닫힘');
            setShowAssignmentSettingsModal(false);
          }}
          user={user}
          currentClass={currentClass}
          onDefaultPointsChange={setAssignmentDefaultPoints}
          currentDefaultPoints={assignmentDefaultPoints}
          onStatusDefaultPointsChange={setAssignmentStatusDefaultPoints}
          currentStatusDefaultPoints={assignmentStatusDefaultPoints}
          onLicenseClick={handleLicensePageNavigate}
          onCategoriesChange={setAssignmentCategories}
          onAssignmentSettingsChange={async (defaultPoints, statusDefaultPoints) => {
            console.log('🔍 과제 설정 저장 시작 (DefaultSet):', { defaultPoints, statusDefaultPoints });
            try {
              if (user && currentClass) {
                await userDataService.saveAssignmentSettings(user.id, currentClass.id, {
                  defaultPoints,
                  statusDefaultPoints,
                  updatedAt: new Date()
                });
                console.log('✅ 과제 설정 저장 완료 (DefaultSet)');
              }
            } catch (error) {
              console.error('❌ 과제 설정 저장 실패:', error);
            }
          }}
          onCoinSettingsChange={async (coinDefaultPoints) => {
            console.log('🔍 코인 설정 저장 시작 (DefaultSet):', coinDefaultPoints);
            try {
              if (user && currentClass) {
                await defaultSetService.updateCoinDefaultPoints(user.id, currentClass.id, coinDefaultPoints);
                
                console.log('✅ 코인 설정 저장 완료 (DefaultSet):', coinDefaultPoints);
                
                // 저장 후 currentClass 상태 업데이트 (UI 반영용)
                setCurrentClass(prevClass => {
                  if (prevClass) {
                    return {
                      ...prevClass,
                      coinSettings: {
                        ...prevClass.coinSettings,
                        defaultPoints: coinDefaultPoints
                      }
                    };
                  }
                  return prevClass;
                });
                
                console.log('🔄 currentClass 상태 업데이트 완료:', { coinDefaultPoints });
              }
            } catch (error) {
              console.error('❌ 코인 설정 저장 실패:', error);
            }
          }}
          currentCoinDefaultPoints={currentClass?.coinSettings?.defaultPoints || 10}
          initialTimeTags={timeTags}
          alarmSets={globalAlarmService?.alarmSets || []}
          onCreateAlarms={handleCreateAlarmsFromTimeTags}
          onTimeTagsSave={(tags) => setTimeTags(tags)}
        />
      )}
      {user && currentClass && (
        <MemberProfileSettingsModal
          isOpen={showMemberProfileSettingsModal}
          onClose={() => setShowMemberProfileSettingsModal(false)}
          user={user}
          currentClass={currentClass}
          currentMemberGroup={currentMemberGroup}
        />
      )}

      {/* 알람 페이지 전용 모달들 - 알람 페이지에서만 표시 */}
      {user && currentPage === 'alarm' && (
        <>
          <NewAlarmModal
            isOpen={showNewAlarmModal}
            onSave={() => setShowNewAlarmModal(false)} // 실제 저장 로직은 AlarmPage에서 콜백으로 처리
            onCancel={handleNewAlarmModalClose}
            timeFormat={timeFormat}
          />
          {/* SetNameModal은 AlarmPage에서 렌더링됨 */}
          <EditSetNameModal
            isOpen={showEditSetNameModal}
            currentName={''} // 실제 이름은 AlarmPage에서 콜백으로 처리
            onSave={() => setShowEditSetNameModal(false)}
            onCancel={handleEditSetNameModalClose}
          />
        </>
      )}

      {/* 전역 알람 팝업 - 모든 페이지에서 표시 */}
      {user && (
        <AlarmPopup
          isVisible={showAlarmPopup}
          alarmTime={currentAlarmInfo?.time || ''}
          alarmType={currentAlarmInfo?.type || '1M'}
          alarmName={currentAlarmInfo?.name}
          phaseMinutes={currentAlarmInfo?.minutesBefore}
          onStopAlarm={handleStopAlarm}
          onClosePopup={handleAlarmPopupClose}
        />
      )}
      
      {/* 전역 종 아이콘 - 모든 페이지에서 표시 */}
      {user && (() => {
        console.log('전역 종 아이콘 렌더링 조건 체크:');
        console.log('currentAlarmInfo:', currentAlarmInfo);
        console.log('showAlarmPopup:', showAlarmPopup);
        if (currentAlarmInfo) {
          console.log('isAlarmTimePassed 결과:', isAlarmTimePassed(currentAlarmInfo.time));
        }
        return currentAlarmInfo && !showAlarmPopup && !isAlarmTimePassed(currentAlarmInfo.time);
      })() && (
        <div style={{
          position: 'fixed',
          bottom: bellIconPosition ? `${bellIconPosition.bottom}px` : (window.innerWidth <= 768 ? '80px' : '60px'),
          right: bellIconPosition ? `${bellIconPosition.right}px` : (window.innerWidth <= 768 ? '20px' : '24px'),
          zIndex: 1000
        }}>
          <button
            onClick={() => {
              setShowAlarmPopup(true);
            }}
            style={{
              background: 'var(--color-accent-strong)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: 40,
              height: 40,
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(var(--color-accent-rgb), 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              transition: 'all 0.3s ease',
              animation: 'pulse 2s infinite',
              outline: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-accent)';
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(var(--color-accent-rgb), 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--color-accent-strong)';
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(var(--color-accent-rgb), 0.3)';
            }}
          >
            <svg 
              width={18}
              height={18}
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M12 2C13.1 2 14 2.9 14 4V5.29C17.03 6.15 19 8.83 19 12V17L21 19V20H3V19L5 17V12C5 8.83 6.97 6.15 10 5.29V4C10 2.9 10.9 2 12 2ZM12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22Z" 
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
      )}


      {/* 좌측 하단: 타이머/뽑기 호버 버튼 (알림/과제/포인트/클래스챗 페이지에서만 표시) */}
      {user && ['alarm', 'assignments', 'points-table', 'chat'].includes(currentPage) && (
        <div
          style={{
            position: 'fixed',
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 60px)',
            left: '24px',
            zIndex: 1000,
            display: 'flex',
            gap: '10px',
            alignItems: 'center'
          }}
        >
          <button
            type="button"
            onClick={() => {
              setShowTimerModal(true);
              setTimerModalHidden(false);
            }}
            aria-label="타이머 열기"
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: isTimerActiveInHover ? '#0D4039' : 'rgba(160,160,160,0.22)',
              border: isTimerActiveInHover ? '1px solid #0D4039' : '1px solid rgba(255,255,255,0.16)',
              color: 'white',
              cursor: 'pointer',
              opacity: isTimerActiveInHover ? 1 : 0.9,
              boxShadow: isTimerActiveInHover
                ? '0 6px 20px rgba(34, 121, 113, 0.35)'
                : '0 2px 8px rgba(0, 0, 0, 0.1)',
              transition: 'opacity 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
              animation: isTimerActiveInHover ? 'timerRunningHoverPulse 1.1s ease-in-out infinite' : undefined,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              outline: 'none'
            }}
            onMouseEnter={(e) => {
              if (isTimerActiveInHover) return;
              e.currentTarget.style.background = 'rgba(160,160,160,0.38)';
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onMouseLeave={(e) => {
              if (isTimerActiveInHover) return;
              e.currentTarget.style.background = 'rgba(160,160,160,0.22)';
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
            }}
          >
            <img
              src="/Timer.svg"
              alt=""
              style={{
                width: 18,
                height: 18,
                filter: 'brightness(0) invert(1)'
              }}
            />
          </button>

          <button
            type="button"
            onClick={() => {
              setShowDiceModal(true);
              setDiceModalHidden(false);
            }}
            aria-label="뽑기 열기"
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'rgba(160,160,160,0.22)',
              border: '1px solid rgba(255,255,255,0.16)',
              color: 'white',
              cursor: 'pointer',
              opacity: 0.9,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              transition: 'opacity 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              outline: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(160,160,160,0.38)';
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(160,160,160,0.22)';
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
            }}
          >
            <img
              src="/Dice.svg"
              alt=""
              style={{
                width: 18,
                height: 18,
                filter: 'brightness(0) invert(1)'
              }}
            />
          </button>
        </div>
      )}

      <TimerModal
        isOpen={showTimerModal}
        isHidden={timerModalHidden}
        onRequestHide={() => setTimerModalHidden(true)}
        onRunningChange={(running) => setIsTimerRunning(running)}
        onClose={() => {
          setTimerModalHidden(false);
          setIsTimerRunning(false);
          setShowTimerModal(false);
        }}
      />

      <DiceModal
        isOpen={showDiceModal}
        isHidden={diceModalHidden}
        onRequestHide={() => setDiceModalHidden(true)}
        onClose={() => {
          setDiceModalHidden(false);
          setShowDiceModal(false);
        }}
        members={classMembers}
        userId={user?.id}
        classId={currentClass?.id}
      />

      {/* 공지사항 팝업 */}
      <AnnouncementModal
        isOpen={showAnnouncementModal}
        content={announcement?.content || ''}
        onClose={handleAnnouncementClose}
        onDontShowToday={handleDontShowToday}
        enableCountdown={isAutoPopup} // 자동 팝업일 때만 카운트다운 활성화
      />

      {/* 공지사항 목록 모달 */}
      {showAnnouncementList && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={handleAnnouncementListClose}
        >
          <div
            style={{
              background: 'var(--color-surface)',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid var(--color-border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: '700',
                color: 'var(--color-text-primary)'
              }}>
                공지사항 목록
              </h2>
              <button
                onClick={handleAnnouncementListClose}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--color-panel)';
                  e.currentTarget.style.color = 'var(--color-text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.color = 'var(--color-text-secondary)';
                }}
              >
                ×
              </button>
            </div>

            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '0'
            }}>
              {announcements.length > 0 ? (
                announcements.map((item, index) => (
                  <div
                    key={item.id}
                    onClick={() => handleAnnouncementClick(item)}
                    style={{
                      padding: '16px 24px',
                      borderBottom: index < announcements.length - 1 ? '1px solid var(--color-border)' : 'none',
                      cursor: 'pointer',
                      transition: 'background 0.2s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--color-panel)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'none';
                    }}
                  >
                    {item.title && (
                      <div style={{
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: 'var(--color-text-primary)',
                        marginBottom: '4px'
                      }}>
                        {item.title}
                      </div>
                    )}
                    <div style={{
                      fontSize: '0.9rem',
                      color: 'var(--color-text-secondary)',
                      lineHeight: '1.4',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}
                    dangerouslySetInnerHTML={{
                      __html: item.content.replace(/<[^>]*>/g, '').substring(0, 100) + (item.content.replace(/<[^>]*>/g, '').length > 100 ? '...' : '')
                    }}
                    />
                    <div style={{
                      fontSize: '0.8rem',
                      color: 'var(--color-text-muted)',
                      marginTop: '4px'
                    }}>
                      {item.createdAt.toLocaleDateString('ko-KR')}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{
                  padding: '40px 24px',
                  textAlign: 'center',
                  color: 'var(--color-text-secondary)',
                  fontSize: '1rem'
                }}>
                  표시할 공지사항이 없습니다.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!pendingAssignmentModalClose}
        onClose={handleDismissAssignmentCloseWarning}
        onCancel={handleCloseAssignmentModalWithoutSave}
        onConfirm={() => {
          void handleSaveAndCloseAssignmentModal();
        }}
        title={['변경된 데이터가 있습니다.', '저장하지 않고 닫으시겠습니까?']}
        cancelText="저장하지 않고 닫기"
        confirmText="저장하고 닫기"
        icon="!"
        iconBackground="var(--status-pending)"
      />

      {/* 약관 동의 모달 */}
      <TermsAgreementModal
        isOpen={showTermsAgreementModal}
        onClose={handleTermsAgreementClose}
        onConfirm={handleTermsAgreementConfirm}
        onWithdraw={handleTermsAgreementWithdraw}
        userEmail={pendingUserForTermsAgreement?.email}
        user={pendingUserForTermsAgreement}
      />
      <GlobalToast />
    </Router>
  );
}

export default App;
